import {SuiCheckboxModule, SuiSelectModule} from '@angular-ex/semantic-ui';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import {LOCALES, TOPIC_KINDS, WEEKDAYS} from '../../../core/constants';
import {Institution} from '../../../core/models/institutions/institutions.model';
import {Topic} from '../../../core/models/topics/topics.model';
import {PopulatedUser} from '../../../core/models/users/users.model';
import {TopicsService} from '../../../core/services/topics/topics.service';
import {splitTopics} from '../../../core/utils/topics.helper';
import {
  onlyAllowedMailDomainsValidator,
} from '../../directives/allowed-mail-domains-validator/allowed-mail-domains-validator.directive';

export interface UserNewsletterFormValues {
  email: string;
  topics: string[];
  subjectAreas: string[];
  days: number[];
  receiveInternalMessages: boolean;
  receiveInstitutionArticles: boolean;
}

@Component({
  selector: 'app-user-newsletter-form',
  templateUrl: './user-newsletter-form.component.html',
  styleUrls: ['./user-newsletter-form.component.css'],
  imports: [
    FormsModule,
    SuiCheckboxModule,
    ReactiveFormsModule,
    SuiSelectModule,
  ],
})
export class UserNewsletterFormComponent implements OnInit, OnChanges {
  @Output() submitForm = new EventEmitter();
  @Input({required: true}) editUser!: PopulatedUser<'institution'>;

  @Input() admin = false;
  @Input() simple = false;

  topics: Topic[] = [];
  subjectAreas: Topic[] = [];

  protected readonly LOCALES = LOCALES;
  protected readonly WEEKDAYS = WEEKDAYS;
  protected readonly TOPIC_KINDS = TOPIC_KINDS;
  protected readonly $localize = $localize;

  public newsletterForm: UntypedFormGroup;

  constructor(
    fb: UntypedFormBuilder,
    private topicsService: TopicsService
  ) {
    this.newsletterForm = fb.group({
      email: [''],
      locale: ['de'],
      subjectAreas: [''],
      topics: [''],
      days: [''],
      receiveInternalMessages: [false],
      receiveInstitutionArticles: [true],
    });
  }

  ngOnInit(): void {
    this.topicsService.getAll({
      select: ['name', 'englishName', 'kind', 'isSubjectArea'],
    }).subscribe(res => {
      const split = splitTopics(res.docs);
      this.topics = split.topics;
      this.subjectAreas = split.subjectAreas;
    });
    this.setValidators();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.editUser) {
      this.updateValues(changes.editUser.currentValue);
    }
    if (changes.admin) {
      this.setValidators();
      this.newsletterForm.updateValueAndValidity();
    }
  }

  public submit(): UserNewsletterFormValues {
    return this.newsletterForm.value;
  }

  public updateValues(user: PopulatedUser<'institution'>): void {
    this.newsletterForm.patchValue({
      locale: user.abonnement?.locale ?? 'de',
      email: user.abonnement?.email ?? '',
      subjectAreas: user.abonnement?.subjectAreas,
      topics: user.abonnement?.topics,
      days: user.abonnement?.days,
      receiveInternalMessages: user.receiveInternalMessages,
      receiveInstitutionArticles: user.abonnement?.receiveInstitutionArticles ?? false,
    });
    if (user.institution) {
      this.setEmailValidators(user.institution);
    }
  }

  setEmailValidators(institution: Institution) {
    // build regular expression string with domains from selected institution
    const regExpString = `^[a-zA-Z0-9_.+-]+@(?:(?:[a-zA-Z0-9-]+\\.)?[a-zA-Z0-9]+\\.)?(${institution.domains.join('|')})$`;

    // set new validator and update form control
    this.newsletterForm.controls.email.setValidators([
      Validators.required,
      Validators.email,
      onlyAllowedMailDomainsValidator(new RegExp(regExpString))
    ]);
    this.newsletterForm.controls.email.updateValueAndValidity();
  }

  public onSubmit(): void {
    this.submitForm.emit(this.newsletterForm.value);
  }

  setValidators() {
    this.newsletterForm.controls.subjectAreas.setValidators(this.admin ? null : Validators.required);
    this.newsletterForm.controls.topics.setValidators(this.admin ? null : Validators.required);
    this.newsletterForm.controls.days.setValidators(this.admin ? null : Validators.required);
  }
}
